import {
  AfterViewInit,
  Component,
  computed,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';

import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {HrTool} from '@frontend/directus';
import {
  ParticipatingToolResponse,
  ProjectChapterType,
  ProjectStepType,
  TenderPhaseDetailsResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {InvitationTableComponent} from '../../../../../../shared/components/table/invitation-table/invitation-table.component';
import {
  InvitationTableTemplateContext,
  InviteToolsResponseOf,
  PhaseInvitationConfig,
  ProviderDecisionReason,
} from '../../../../../../shared/components/table/invitation-table/invitation-table.types';
import {RfqParticipatingTool} from './step-8-3.types';
import {InvitationDataLoaderService} from '../../../../../../shared/services/invitation-data-loader.service';
import {ProviderTotalScoreResult} from '../../../../../../shared/services/provider-total-score-calculation.service';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';
import {ResultScoreCellComponent} from '../../../../../../shared/components/result-score-cell/result-score-cell.component';
import {OverallScoreService} from '../../../../../../shared/services/overall-score.service';
import {firstValueFrom} from 'rxjs';
import {DecisionReasonModalComponent} from '../../../../../../shared/components/decision-reason-modal/decision-reason-modal.component';

@Component({
  imports: [
    TranslocoPipe,
    InvitationTableComponent,
    Button,
    Tooltip,
    PreviousStepIncompleteNoticeComponent,
    ResultScoreCellComponent,
    DecisionReasonModalComponent,
  ],
  templateUrl: './step-8-3-invitation-list.component.html',
})
export class Step83InvitationListComponent
  extends ProjectBaseStepComponent
  implements AfterViewInit
{
  private readonly transloco = inject(TranslocoService);
  private readonly invitationDataLoader = inject(InvitationDataLoaderService);
  private readonly overallScoreService = inject(OverallScoreService);
  readonly totalResultTemplate =
    viewChild<TemplateRef<InvitationTableTemplateContext<RfqParticipatingTool>>>(
      'totalResultTemplate',
    );

  readonly reasonTemplate =
    viewChild<TemplateRef<InvitationTableTemplateContext<RfqParticipatingTool>>>('reasonTemplate');

  readonly invitationTable = viewChild(InvitationTableComponent);

  protected readonly currentPhase = signal<TenderPhaseDetailsResponse | null>(null);
  private readonly rfiProviderScores = signal<Map<string, ProviderTotalScoreResult>>(new Map());
  private readonly rfpProviderScores = signal<Map<string, ProviderTotalScoreResult>>(new Map());
  private readonly rfdProviderPercentages = signal<Map<string, number>>(new Map());
  protected readonly rfiWeightSum = signal(0);
  protected readonly rfpWeightSum = signal(0);
  protected readonly rfdWeightSum = signal(0);
  private readonly totalResultTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfqParticipatingTool>
  > | null>(null);
  private readonly reasonTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfqParticipatingTool>
  > | null>(null);

  protected readonly reasonModalVisible = signal(false);
  protected readonly selectedReasonTool = signal<RfqParticipatingTool | null>(null);

  private readonly RFD_COMPLETION_STEP: {chapterId: ProjectChapterType; stepId: ProjectStepType} = {
    chapterId: ProjectChapterType.RFD,
    stepId: ProjectStepType.RFD_PROVIDER_RESULTS,
  };

  protected openReasonModal(item: RfqParticipatingTool): void {
    this.selectedReasonTool.set(item);
    this.reasonModalVisible.set(true);
  }

  protected closeReasonModal(): void {
    this.reasonModalVisible.set(false);
    this.selectedReasonTool.set(null);
  }

  protected onReasonSaved(updatedReason: unknown): void {
    const reason = updatedReason as {
      participatingToolId?: number;
      mainReasonId?: string | null;
      detailReasonIds?: string[];
      generatedText?: string;
      comment?: string;
    };

    const tool = this.selectedReasonTool();
    if (tool && reason.mainReasonId) {
      const updatedDecisionReason: ProviderDecisionReason = {
        mainReasonId: reason.mainReasonId,
        detailReasonIds: reason.detailReasonIds ?? [],
        generatedText: reason.generatedText ?? '',
        comment: reason.comment ?? null,
        hasValue: true,
      };

      this.invitationTable()?.updateToolItemById(tool.id, {
        decisionReason: updatedDecisionReason,
      } as Partial<RfqParticipatingTool>);
    }

    this.closeReasonModal();
  }

  protected readonly isStepPrerequisiteCompleted = computed(() => {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFQ, stepId: ProjectStepType.RFQ_INVITATION_PREVIEW},
    ]);
  });

  protected readonly rfqPhaseConfig = computed((): PhaseInvitationConfig<RfqParticipatingTool> => ({
    phaseType: TenderPhaseType.RFQ,
    timeFrameNumber: 4,
    tableConfig: {
      columns: [
        {
          field: 'toolProvider.name',
          header: 'project.steps.invitationList.provider',
          sortable: true,
          width: '20%',
        },
        {
          field: 'translatedName',
          header: 'project.steps.invitationList.tool',
          sortable: true,
          width: '20%',
        },
        {
          field: 'totalResult',
          header: 'project.steps.invitationList.totalResult',
          width: '18%',
          template: this.totalResultTemplateSignal() ?? undefined,
        },
        {
          field: 'selection',
          header: 'project.steps.invitationList.selection',
          sortable: true,
          sortField: 'selection',
          width: '12%',
        },
        {
          field: 'decisionReason',
          header: 'project.steps.invitationList.reason',
          width: '16%',
          template: this.reasonTemplateSignal() ?? undefined,
        },
        {
          field: 'status',
          header: 'project.steps.invitationList.status',
          sortable: true,
          sortField: 'status',
          width: '10%',
        },
      ],
      emptyMessage: 'common.noData',
    },
    timeframeConfig: {
      tooltipKeys: {
        plannedShippingDate: 'project.steps.rfq.invitationList.plannedShippingDate',
        plannedSubmissionDate: 'project.steps.rfq.invitationList.plannedSubmissionDate',
      },
    },
    prerequisiteChecker: () => this.prerequisitesMet(),
    stepLockedFn: () => this.isCurrentStepLocked(),
    dataLoader: (projectId) => this.loadRfqData(projectId),
    translationKeys: {
      title: 'project.steps.rfq.invitationList.title',
    },
  }));

  ngAfterViewInit(): void {
    const totalResultTemplate = this.totalResultTemplate();
    if (totalResultTemplate) {
      this.totalResultTemplateSignal.set(totalResultTemplate);
    }

    const reasonTemplate = this.reasonTemplate();
    if (reasonTemplate) {
      this.reasonTemplateSignal.set(reasonTemplate);
    }
  }

  onCurrentPhaseChange(phase: TenderPhaseDetailsResponse | null): void {
    this.currentPhase.set(phase);
  }

  private readonly prerequisitesMet = computed(() => {
    this.stepState.chaptersWithStatuses();

    const chaptersCompleted = [
      ProjectChapterType.PROJECT_SETUP,
      ProjectChapterType.PRINCIPLE_DEFINITION,
      ProjectChapterType.ACTUAL_SYSTEMS,
      ProjectChapterType.EVALUATION_CRITERIA,
      ProjectChapterType.RFI,
      ProjectChapterType.RFP,
      ProjectChapterType.RFD,
    ].every((chapterId) => this.stepState.isChapterCompleted(chapterId));

    const specificStepsCompleted = this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFQ, stepId: ProjectStepType.RFQ_PROPOSAL_PARAMETER},
      {chapterId: ProjectChapterType.RFQ, stepId: ProjectStepType.RFQ_INVITATION_PREVIEW},
    ]);

    return chaptersCompleted && specificStepsCompleted;
  });

  private async loadRfqData(
    projectId: number,
  ): Promise<InviteToolsResponseOf<RfqParticipatingTool>> {
    const [{directusHrTools, participatingToolsResponse}, phaseScores] = await Promise.all([
      firstValueFrom(
        this.invitationDataLoader.loadParticipatingToolsData(projectId, TenderPhaseType.RFQ),
      ),
      this.overallScoreService.loadRfiRfpRfdScores(projectId),
    ]);

    this.rfiProviderScores.set(phaseScores.rfiTotals);
    this.rfpProviderScores.set(phaseScores.rfpTotals);
    this.rfdProviderPercentages.set(phaseScores.rfdPercentages);
    this.rfiWeightSum.set(phaseScores.rfiWeight);
    this.rfpWeightSum.set(phaseScores.rfpWeight);
    this.rfdWeightSum.set(phaseScores.rfdWeight);

    const toolItems = participatingToolsResponse.tools
      .map((rfqTool: ParticipatingToolResponse) => {
        const hrTool = directusHrTools.find((tool: HrTool) => tool.id === rfqTool.hrToolDirectusId);
        return hrTool ? this.mapToParticipatingTool(hrTool, rfqTool) : null;
      })
      .filter((tool: RfqParticipatingTool | null): tool is RfqParticipatingTool => tool !== null);

    return {
      tools: toolItems,
      currentPhase: participatingToolsResponse.currentPhase,
      maxAllowedParticipants: participatingToolsResponse.maxAllowedParticipants,
      calculatedPhaseSubmissionDate: participatingToolsResponse.calculatedPhaseSubmissionDate,
    };
  }

  private mapToParticipatingTool(
    tool: HrTool,
    rfqTool: ParticipatingToolResponse,
  ): RfqParticipatingTool {
    const lang = this.transloco.getActiveLang();
    const translation = tool.translations.find((t) => t.language === lang);
    const rfiScore = this.rfiProviderScores().get(tool.id.toString());
    const rfpScore = this.rfpProviderScores().get(tool.id.toString());
    const rfdPercentage = this.rfdProviderPercentages().get(tool.id.toString());

    const hasDecisionReason = !!rfqTool.rfqMainReasonUuid;

    return {
      ...tool,
      translatedName: (translation?.name?.trim() !== '' ? translation?.name : tool.name) ?? null,
      participatingToolId: rfqTool.id,
      selection: rfqTool.rfqSelection ?? null,
      status: rfqTool.rfqStatus,
      rfiPercentage: rfiScore?.percentage ?? null,
      rfpPercentage: rfpScore?.percentage ?? null,
      rfdPercentage: rfdPercentage ?? null,
      decisionReason: hasDecisionReason
        ? {
            mainReasonId: rfqTool.rfqMainReasonUuid ?? null,
            detailReasonIds: rfqTool.rfqDetailReasonUuids ?? [],
            generatedText: rfqTool.rfqReasonText ?? '',
            comment: rfqTool.rfqReasonComment ?? null,
            hasValue: true,
          }
        : null,
    };
  }

  navigateToRfd() {
    this.stepState.setActiveStep(
      this.RFD_COMPLETION_STEP.chapterId,
      this.RFD_COMPLETION_STEP.stepId,
    );
  }
}
